'use client';

import { useRef, useState } from 'react';

import { cantonLabels } from '@selenos/vertical-spitex';

import { trackCostCalculatorUsed } from '~/lib/analytics/posthog-events';

/**
 * KLV hourly rates (CHF) per service category.
 * Source: Krankenpflege-Leistungsverordnung (KLV).
 */
const KVG_RATES = {
  grundpflege: 52.6,
  behandlung: 63.0,
  abklaerung: 76.9,
} as const;

/**
 * Maximum patient co-payment per day (CHF).
 */
const MAX_COPAY_PER_DAY = 15.35;

/**
 * Map care type selection to the relevant KVG rate.
 * Non-KVG services use a fixed private rate estimate.
 */
const careTypeConfig: Record<
  string,
  {
    label: string;
    kvgCovered: boolean;
    rateKey?: keyof typeof KVG_RATES;
    privateRate?: number;
  }
> = {
  grundpflege: {
    label: 'Grundpflege',
    kvgCovered: true,
    rateKey: 'grundpflege',
  },
  behandlung: {
    label: 'Untersuchung und Behandlung',
    kvgCovered: true,
    rateKey: 'behandlung',
  },
  abklaerung: {
    label: 'Abklaerung und Beratung',
    kvgCovered: true,
    rateKey: 'abklaerung',
  },
  hauswirtschaft: {
    label: 'Hauswirtschaftliche Hilfe',
    kvgCovered: false,
    privateRate: 50,
  },
  begleitdienst: {
    label: 'Begleitdienst',
    kvgCovered: false,
    privateRate: 45,
  },
};

/** Canton keys in alphabetical order. */
const cantonKeys = [
  'AG',
  'AI',
  'AR',
  'BE',
  'BL',
  'BS',
  'FR',
  'GE',
  'GL',
  'GR',
  'JU',
  'LU',
  'NE',
  'NW',
  'OW',
  'SG',
  'SH',
  'SO',
  'SZ',
  'TG',
  'TI',
  'UR',
  'VD',
  'VS',
  'ZG',
  'ZH',
] as const;

function formatCHF(amount: number): string {
  return `CHF ${amount.toLocaleString('de-CH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function CostCalculator({ verticalSlug }: { verticalSlug: string }) {
  const [canton, setCanton] = useState<string>('ZH');
  const [careType, setCareType] = useState<string>('grundpflege');
  const [hoursPerWeek, setHoursPerWeek] = useState<number>(5);
  const hasFiredEvent = useRef(false);

  const fireOnce = (c: string, ct: string, h: number) => {
    if (!hasFiredEvent.current) {
      hasFiredEvent.current = true;
      trackCostCalculatorUsed(c, ct, h, verticalSlug);
    }
  };

  const config = careTypeConfig[careType];
  const hoursPerMonth = hoursPerWeek * 4.33;

  let monthlyCost = 0;
  let kvgCoverage = 0;
  let patientCopay = 0;
  let restfinanzierung = 0;
  let isKvgCovered = false;

  if (config) {
    isKvgCovered = config.kvgCovered;

    if (isKvgCovered && config.rateKey) {
      const hourlyRate = KVG_RATES[config.rateKey];
      monthlyCost = hoursPerMonth * hourlyRate;

      // Patient co-pay: max CHF 15.35/day, roughly 30 days/month
      // But only on days with actual care visits
      const daysPerMonth = Math.min(
        hoursPerWeek >= 7 ? 30 : hoursPerWeek * 4.33,
        30,
      );
      patientCopay = Math.min(
        daysPerMonth * MAX_COPAY_PER_DAY,
        monthlyCost * 0.2,
      );

      // Insurance covers the KLV rate
      // Restfinanzierung covers gap (simplified: ~20-30% of total)
      kvgCoverage = monthlyCost * 0.55;
      restfinanzierung = monthlyCost - kvgCoverage - patientCopay;
      if (restfinanzierung < 0) restfinanzierung = 0;
    } else {
      // Non-KVG services: full cost to patient
      const rate = config.privateRate ?? 50;
      monthlyCost = hoursPerMonth * rate;
      patientCopay = monthlyCost;
    }
  }

  return (
    <section className="bg-muted/50 w-full px-4 py-16 sm:py-20">
      <div className="mx-auto max-w-6xl">
        <p className="text-muted-foreground mb-2 text-center text-sm tracking-[0.2em] uppercase">
          Kostenrechner
        </p>
        <h2 className="text-foreground mb-4 text-center text-2xl font-bold sm:text-3xl">
          Was kostet Spitex-Pflege?
        </h2>
        <p className="text-muted-foreground mx-auto mb-12 max-w-xl text-center text-base">
          Berechnen Sie Ihre geschaetzten monatlichen Kosten. Aerztlich
          verordnete Pflegeleistungen werden grossteils von der Krankenkasse
          uebernommen.
        </p>

        {/* Inputs */}
        <div className="mx-auto max-w-2xl">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            {/* Canton */}
            <div>
              <label
                htmlFor="cost-canton"
                className="text-foreground block text-sm font-medium"
              >
                Kanton
              </label>
              <select
                id="cost-canton"
                value={canton}
                onChange={(e) => {
                  setCanton(e.target.value);
                  fireOnce(e.target.value, careType, hoursPerWeek);
                }}
                className="bg-card text-foreground border-border mt-1 block w-full rounded-lg border px-3 py-2.5 shadow-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
              >
                {cantonKeys.map((key) => (
                  <option key={key} value={key}>
                    {cantonLabels[key] ?? key}
                  </option>
                ))}
              </select>
            </div>

            {/* Care type */}
            <div>
              <label
                htmlFor="cost-care-type"
                className="text-foreground block text-sm font-medium"
              >
                Pflegeart
              </label>
              <select
                id="cost-care-type"
                value={careType}
                onChange={(e) => {
                  setCareType(e.target.value);
                  fireOnce(canton, e.target.value, hoursPerWeek);
                }}
                className="bg-card text-foreground border-border mt-1 block w-full rounded-lg border px-3 py-2.5 shadow-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
              >
                {Object.entries(careTypeConfig).map(([key, cfg]) => (
                  <option key={key} value={key}>
                    {cfg.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Hours per week */}
            <div>
              <label
                htmlFor="cost-hours"
                className="text-foreground block text-sm font-medium"
              >
                Stunden pro Woche
              </label>
              <input
                id="cost-hours"
                type="number"
                min={1}
                max={40}
                value={hoursPerWeek}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);

                  if (!isNaN(val) && val >= 1 && val <= 40) {
                    setHoursPerWeek(val);
                  }

                  fireOnce(canton, careType, val || hoursPerWeek);
                }}
                className="bg-card text-foreground border-border mt-1 block w-full rounded-lg border px-3 py-2.5 shadow-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Result */}
        <div className="mx-auto mt-10 max-w-2xl">
          <div className="bg-card rounded-2xl border p-6 sm:p-8">
            <div className="mb-6 text-center">
              <p className="text-muted-foreground text-sm">
                Geschaetzte monatliche Gesamtkosten
              </p>
              <p
                className="mt-2 text-4xl font-bold sm:text-5xl"
                style={{ color: 'var(--landing-primary)' }}
              >
                {formatCHF(monthlyCost)}
              </p>
              <p className="text-muted-foreground mt-1 text-sm">
                {hoursPerMonth.toFixed(0)} Stunden/Monat
              </p>
            </div>

            {isKvgCovered ? (
              <>
                <div className="space-y-3">
                  {/* KVG coverage bar */}
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">
                      Krankenkasse (KVG)
                    </span>
                    <span className="text-foreground text-sm font-semibold">
                      {formatCHF(kvgCoverage)}
                    </span>
                  </div>
                  <div className="bg-muted h-3 w-full overflow-hidden rounded-full">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${monthlyCost > 0 ? (kvgCoverage / monthlyCost) * 100 : 0}%`,
                        backgroundColor: 'var(--landing-primary)',
                      }}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">
                      Kanton/Gemeinde (Restfinanzierung)
                    </span>
                    <span className="text-foreground text-sm font-semibold">
                      {formatCHF(restfinanzierung)}
                    </span>
                  </div>
                  <div className="bg-muted h-3 w-full overflow-hidden rounded-full">
                    <div
                      className="h-full rounded-full bg-blue-400 transition-all"
                      style={{
                        width: `${monthlyCost > 0 ? (restfinanzierung / monthlyCost) * 100 : 0}%`,
                      }}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-foreground text-sm font-medium">
                      Ihr Anteil (Patientenbeteiligung)
                    </span>
                    <span
                      className="text-sm font-bold"
                      style={{ color: 'var(--landing-accent)' }}
                    >
                      {formatCHF(patientCopay)}
                    </span>
                  </div>
                  <div className="bg-muted h-3 w-full overflow-hidden rounded-full">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${monthlyCost > 0 ? (patientCopay / monthlyCost) * 100 : 0}%`,
                        backgroundColor: 'var(--landing-accent)',
                      }}
                    />
                  </div>
                </div>

                <p className="text-muted-foreground mt-4 text-xs">
                  Max. CHF 15.35 Patientenbeteiligung pro Tag. Zusaetzlich:
                  Franchise (CHF 300-2,500/Jahr) und Selbstbehalt (10%).
                  Aerztliche Verordnung erforderlich.
                </p>
              </>
            ) : (
              <div className="text-center">
                <div className="inline-flex items-center rounded-lg bg-amber-50 px-4 py-2 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                  Diese Leistung ist nicht durch die Grundversicherung (KVG)
                  gedeckt. Die Kosten tragen Sie selbst.
                </div>
                <p className="text-muted-foreground mt-4 text-xs">
                  Einige Zusatzversicherungen uebernehmen einen Teil der Kosten
                  fuer hauswirtschaftliche Hilfe und Begleitdienste. Pruefen Sie
                  Ihre Police oder kontaktieren Sie Ihre Krankenkasse.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-muted-foreground mt-8 text-center text-xs">
          Richtwerte basierend auf den KLV-Tarifen. Die tatsaechlichen Kosten
          haengen vom Anbieter, Kanton und individuellen Pflegebedarf ab.
          Kostenlose Erstberatung bei Ihrer Anfrage.
        </p>
      </div>
    </section>
  );
}
